import type {
  ReactFlowNodeData,
  IdeaNodeData,
  VentureNodeData,
  CollapsedHierarchyNode,
} from '../types/mindmap-components'
import type { StakeholderRatings } from '@bandos/shared'

/**
 * Formats stakeholder ratings into readable text
 */
function formatStakeholderRatings(ratings: StakeholderRatings): string {
  const lines: string[] = []

  // Stakeholder ratings
  if (ratings.stakeholders.length > 0) {
    ratings.stakeholders.forEach((stakeholder, index) => {
      if (index > 0) {
        lines.push('')
      }
      lines.push(`${stakeholder.name} - ${stakeholder.role} (${stakeholder.rating}/10)`)
      lines.push('')
      lines.push('What works:')
      lines.push(`  ${stakeholder.whatWorks}`)
      lines.push('')
      lines.push('Main risks:')
      lines.push(`  ${stakeholder.mainRisks}`)
      lines.push('')
      lines.push('Suggestions:')
      lines.push(`  ${stakeholder.suggestions}`)
      lines.push('')
      lines.push('Question:')
      lines.push(`  ${stakeholder.question}`)
    })
  }

  return lines.join('\n').trim()
}

/**
 * Formats a company node into readable text
 */
function formatVentureNode(nodeData: VentureNodeData): string {
  const lines: string[] = []

  // Venture/project name as title
  if (nodeData.companyName) {
    lines.push(nodeData.companyName)
    lines.push('')
  }

  // What are you building?
  if (nodeData.businessModelAndValueProp) {
    lines.push('What are you building:')
    lines.push(nodeData.businessModelAndValueProp)
    lines.push('')
  }

  // Product Type / Format
  if (nodeData.productType) {
    lines.push('Format:')
    lines.push(nodeData.productType)
    lines.push('')
  }

  // Available Strengths
  if (nodeData.capabilitiesAndAssets) {
    lines.push('Available Strengths:')
    lines.push(nodeData.capabilitiesAndAssets)
    lines.push('')
  }

  // Who is it for?
  if (nodeData.goals) {
    lines.push('Who is it for:')
    lines.push(nodeData.goals)
    lines.push('')
  }

  // Project Boundaries
  if (nodeData.hardConstraints) {
    lines.push('Project Boundaries:')
    lines.push(nodeData.hardConstraints)
  }

  return lines.join('\n').trim()
}

/**
 * Formats an idea node into readable text
 */
function formatIdeaNode(nodeData: IdeaNodeData): string {
  const lines: string[] = []

  // Main label
  if (nodeData.label) {
    lines.push(nodeData.label)
    lines.push('')
  }

  // Labels
  if (nodeData.labels && nodeData.labels.length > 0) {
    const labelTexts = nodeData.labels.map((label) => label.label_text).join(', ')
    lines.push(`Labels: ${labelTexts}`)
    lines.push('')
  }

  // Stakeholder ratings
  if (nodeData.stakeholderRating) {
    lines.push('Stakeholder Feedback:')
    lines.push('')
    lines.push(formatStakeholderRatings(nodeData.stakeholderRating))
  }

  return lines.join('\n').trim()
}

/**
 * Formats node content into readable text based on node category
 * @param nodeData - The node data to format
 * @returns Formatted text string
 */
export function formatNodeContent(nodeData: ReactFlowNodeData): string {
  switch (nodeData.category) {
    case 'need':
      return nodeData.label ?? ''

    case 'idea':
      return formatIdeaNode(nodeData as IdeaNodeData)

    case 'venture':
      return formatVentureNode(nodeData as VentureNodeData)

    case 'information':
    case 'summarized':
    case 'storyboard':
    case 'insight':
    default:
      // For information nodes or any other type, just return the label
      return nodeData.label || ''
  }
}

/**
 * Formats a single hierarchy node label based on its category
 * @param node - The hierarchy node to format
 */
function formatHierarchyNodeLabel(node: CollapsedHierarchyNode): string {
  return node.label || `${node.category} node`
}

/**
 * Recursively formats a collapsed hierarchy into indented text
 * Creates a tree structure with bullet points and indentation suitable for Word
 * @param node - The hierarchy node to format
 * @param depth - Current indentation depth
 */
function formatHierarchyNode(node: CollapsedHierarchyNode, depth: number = 0): string[] {
  const lines: string[] = []
  const indent = '    '.repeat(depth) // 4 spaces per level for Word compatibility

  // Format the node's label
  let line = `${indent}• ${formatHierarchyNodeLabel(node)}`

  // Add labels/tags if present
  if (node.labels && node.labels.length > 0) {
    const tagTexts = node.labels.map((l) => l.label_text).join(', ')
    line += ` [${tagTexts}]`
  }

  lines.push(line)

  // Recursively format children
  for (const child of node.children) {
    lines.push(...formatHierarchyNode(child, depth + 1))
  }

  return lines
}

/**
 * Counts total nodes in a hierarchy (for the title)
 */
function countNodes(node: CollapsedHierarchyNode): number {
  return 1 + node.children.reduce((sum, child) => sum + countNodes(child), 0)
}

/**
 * Formats a collapsed hierarchy into readable text for clipboard
 * Creates a nicely formatted tree structure with:
 * - Title showing node count
 * - Bullet points for each node
 * - Indentation for child nodes
 * - Tags shown in brackets
 * @param hierarchy - The collapsed hierarchy to format
 * @returns Formatted text string suitable for pasting into Word
 */
export function formatCollapsedHierarchy(hierarchy: CollapsedHierarchyNode): string {
  const totalNodes = countNodes(hierarchy)
  const lines: string[] = []

  // Title
  lines.push(`Collapsed Hierarchy (${totalNodes} node${totalNodes !== 1 ? 's' : ''})`)
  lines.push('')

  // Format the tree
  lines.push(...formatHierarchyNode(hierarchy, 0))

  return lines.join('\n')
}
